import { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  AvatarGroup,
  Avatar,
  Tooltip,
} from "@mui/material";

const GOLD = "#E9C66B";

function Logo({ micree = false }) {
  const [failed, setFailed] = useState(false);

  return failed ? (
    <Typography
      sx={{
        color: "#17130B",
        fontWeight: 900,
        fontSize: micree ? 11 : 24,
        fontStyle: "italic",
      }}
    >
      {micree ? "MICREE" : "VR CUP"}
    </Typography>
  ) : (
    <Box
      component="img"
      src={micree ? "/micree.svg" : "/vrcup/logo.png"}
      alt={micree ? "Micree" : "VR Cup"}
      onError={() => setFailed(true)}
      sx={{
        display: "block",
        width: "100%",
        height: micree ? 16 : 78,
        objectFit: "contain",
      }}
    />
  );
}

const anonymize = (name = "") =>
  `${String(name).split("@")[0].split(/[._-]/)[0]}...`;

export default function TournamentEvent({ event }) {
  const meta = event.meta ?? {};
  const players = event.players ?? [];
  const maximum = Number(meta.MaxPlayers);
  const hasMaximum = Number.isFinite(maximum) && maximum > 0;
  const occupancy = hasMaximum
    ? Math.min(100, (players.length / maximum) * 100)
    : 0;

  const avatars = [
    ...(meta.participants ?? []).map((person, index) => ({
      ...person,
      key: `participant-${person.id ?? index}`,
    })),
    ...players.map((person, index) => ({
      ...person,
      key: `player-${person.id ?? index}`,
    })),
  ];

  return (
    <Box
      sx={{
        position: "relative",
        isolation: "isolate",
        width: "100%",
        height: 120,
        maxHeight: 120,
        my: "3px",
        boxSizing: "border-box",
        display: "grid",
        gridTemplateColumns: "minmax(70px, 34%) minmax(0, 1fr)",
        overflow: "hidden",
        borderRadius: "6px",
        color: "#FAFAF7",
        background: "linear-gradient(115deg, #bb9234, #204e62  55%)",
        border: "1px solid rgba(233,198,107,.28)",
        boxShadow: "0 4px 12px rgba(0,0,0,.18)",

        "&::before": {
          content: '"VR"',
          position: "absolute",
          right: -5,
          bottom: -26,
          fontFamily: '"Arial Black", Arial, sans-serif',
          fontSize: 118,
          fontWeight: 900,
          fontStyle: "italic",
          lineHeight: 1,
          letterSpacing: -12,
          color: "rgba(255,255,255,.035)",
          pointerEvents: "none",
          zIndex: -1,
        },

        "&::after": {
          content: '""',
          position: "absolute",
          right: 10,
          top: 0,
          width: 34,
          height: 3,
          bgcolor: GOLD,
          transform: "skewX(-28deg)",
          pointerEvents: "none",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          pl: 1,
          pr: 2,
          py: 0.75,

          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, #B88B35 0%, #F5DE98 45%, #D5AC50 100%)",
            clipPath: "polygon(0 0, 100% 0, 86% 100%, 0 100%)",
            zIndex: -1,
          },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 125 }}>
          <Logo />
        </Box>

        <Box sx={{ width: "70%", maxWidth: 62 }}>
          <Logo micree />
        </Box>
      </Box>

      <Box
        sx={{
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          py: 1,
          pr: 1.25,
          pl: 0.5,
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{ flexShrink: 0 }}
        >
          <Box
            sx={{
              width: 12,
              height: 2,
              bgcolor: GOLD,
              transform: "skewX(-25deg)",
              flexShrink: 0,
            }}
          />

          <Typography
            noWrap
            sx={{
              color: GOLD,
              fontSize: 8,
              lineHeight: "12px",
              fontWeight: 800,
              letterSpacing: 1.8,
            }}
          >
            TURNIEJ VR
          </Typography>
        </Stack>

        <Typography
          title={event.title}
          noWrap
          sx={{
            mt: 0.5,
            fontFamily: '"Arial Black", Arial, sans-serif',
            fontSize: 17,
            lineHeight: "21px",
            fontWeight: 900,
            fontStyle: "italic",
            textTransform: "uppercase",
            letterSpacing: -0.6,
            flexShrink: 0,
          }}
        >
          {event.title}
        </Typography>

        <Typography
          title={meta.scenario}
          noWrap
          sx={{
            fontSize: 10,
            lineHeight: "15px",
            color: "#B6B3AA",
            flexShrink: 0,
          }}
        >
          {meta.scenario || "Scenariusz turniejowy"}
        </Typography>

        {meta.description && (
          <Typography
            title={meta.description}
            noWrap
            sx={{
              fontSize: 8,
              lineHeight: "12px",
              color: "#85847F",
              flexShrink: 0,
            }}
          >
            {meta.description}
          </Typography>
        )}

        <Box sx={{ mt: "auto", pt: 0.5, flexShrink: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={0.5}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  fontSize: 10,
                  lineHeight: "13px",
                  fontWeight: 800,
                  color: GOLD,
                }}
              >
                {players.length}
                {hasMaximum ? ` / ${maximum}` : ""}
                <Box
                  component="span"
                  sx={{
                    ml: 0.5,
                    color: "#B6B3AA",
                    fontSize: 8,
                    fontWeight: 500,
                  }}
                >
                  GRACZY
                </Box>
              </Typography>

              {meta.RequiredPlayersToStart != null && (
                <Typography
                  noWrap
                  sx={{
                    fontSize: 8,
                    lineHeight: "10px",
                    color: "#85847F",
                  }}
                >
                  Min. {meta.RequiredPlayersToStart} do startu
                </Typography>
              )}
            </Box>

            {avatars.length > 0 && (
              <AvatarGroup
                max={3}
                sx={{
                  flexShrink: 0,
                  "& .MuiAvatar-root": {
                    width: 17,
                    height: 17,
                    fontSize: 8,
                    border: "2px solid #111214",
                    bgcolor: "#51452A",
                    color: "#F5DE98",
                  },
                }}
              >
                {avatars.map((person) => (
                  <Tooltip key={person.key} title={anonymize(person.name)}>
                    <Avatar alt={anonymize(person.name)} src={person.avatar} />
                  </Tooltip>
                ))}
              </AvatarGroup>
            )}
          </Stack>

          {hasMaximum && (
            <Box
              role="progressbar"
              aria-label="Zapełnienie miejsc"
              aria-valuenow={players.length}
              aria-valuemin={0}
              aria-valuemax={Math.max(maximum, players.length)}
              sx={{
                mt: 0.5,
                height: 3,
                bgcolor: "rgba(233,198,107,.12)",
                overflow: "hidden",
                transform: "skewX(-20deg)",
              }}
            >
              <Box
                sx={{
                  width: `${occupancy}%`,
                  height: "100%",
                  background: "linear-gradient(90deg, #B88B35, #F5DE98)",
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
